import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getMenuThemeLatest, saveMenuTheme } from '../../Chores/request';

// Async thunks
export const loadMenuThemeLatest = createAsyncThunk('menuTheme/loadLatest', async (_, { rejectWithValue }) => {
  try {
    const data = await getMenuThemeLatest();
    return data; // axios returns already the data payload
  } catch (e) {
    return rejectWithValue(e.message || 'Load failed');
  }
});

export const persistMenuTheme = createAsyncThunk('menuTheme/persist', async (tree, { rejectWithValue }) => {
  try {
    await saveMenuTheme(tree); // tree already transformed by caller to server schema
    return Date.now();
  } catch (e) {
    return rejectWithValue(e.message || 'Save failed');
  }
});

// Helper utilities
const cloneNode = (node) => ({
  ...node,
  children: node.children ? node.children.map(cloneNode) : []
});

const normalizeOrder = (nodes = []) =>
  nodes.map((node, idx) => ({
    ...node,
    order: idx,
    children: node.children ? normalizeOrder(node.children) : []
  }));

const renameNode = (nodes, { id, newLabel, keyOverride, finalizeDraft }) =>
  nodes.map((node) => {
    if (node.id === id) {
      const next = { ...node, name: newLabel, label: newLabel };
      if (keyOverride) {
        next.key = keyOverride;
      }
      if (finalizeDraft) {
        next.isDraft = false;
      }
      return next;
    }
    if (node.children && node.children.length) {
      return { ...node, children: renameNode(node.children, { id, newLabel, keyOverride, finalizeDraft }) };
    }
    return node;
  });

const resolveHiddenFlag = (node) => Boolean(node?.extraProperties?.isHidden);

const syncExtraProperties = (node, extraProperties = {}) => {
  const nextExtra = { ...extraProperties };
  const resolvedPath = nextExtra.path ?? node.path ?? '';
  const resolvedComponent = nextExtra.component ?? node.component ?? '';
  const resolvedProjectId = Object.prototype.hasOwnProperty.call(nextExtra, 'projectId') ? nextExtra.projectId : (Object.prototype.hasOwnProperty.call(node, 'projectId') ? node.projectId : null);
  return {
    ...node,
    path: resolvedPath,
    component: resolvedComponent,
    projectId: resolvedProjectId,
    extraProperties: {
      ...nextExtra,
      path: resolvedPath,
      component: resolvedComponent,
      projectId: resolvedProjectId
    }
  };
};

const applyHiddenFlag = (node, hidden) => {
  const extra = { ...(node.extraProperties || {}) };
  extra.isHidden = hidden;
  return syncExtraProperties(node, extra);
};

const toggleNodeHidden = (nodes, id, hidden) =>
  nodes.map((node) => {
    if (node.id === id) {
      return applyHiddenFlag(node, hidden);
    }
    if (node.children && node.children.length) {
      return { ...node, children: toggleNodeHidden(node.children, id, hidden) };
    }
    return node;
  });

const toggleChildrenVisibility = (nodes, parentId, visibility = {}) =>
  nodes.map((node) => {
    if (node.id === parentId) {
      const children = (node.children || []).map((child) => {
        const nextHidden = Object.prototype.hasOwnProperty.call(visibility, child.id) ? !visibility[child.id] : resolveHiddenFlag(child);
        return applyHiddenFlag(child, nextHidden);
      });
      return { ...node, children };
    }
    if (node.children && node.children.length) {
      return { ...node, children: toggleChildrenVisibility(node.children, parentId, visibility) };
    }
    return node;
  });

const moveNodeInSiblings = (nodes, id, direction) => {
  const findAndMove = (list) => {
    const idx = list.findIndex((n) => n.id === id);
    if (idx !== -1) {
      const next = [...list];
      if (direction === 'up' && idx > 0) {
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      } else if (direction === 'down' && idx < next.length - 1) {
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      }
      return normalizeOrder(next);
    }
    return list.map((node) => {
      if (node.children && node.children.length) {
        return { ...node, children: findAndMove(node.children) };
      }
      return node;
    });
  };
  return findAndMove(nodes);
};

const findNodeById = (nodes, id) => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children && node.children.length) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const isDescendantNode = (node, targetId) => {
  if (!node || !node.children) return false;
  return node.children.some((child) => child.id === targetId || isDescendantNode(child, targetId));
};

const detachNode = (nodes, id) => {
  const walk = (list) => {
    let removed = null;
    const next = [];

    for (const node of list) {
      if (node.id === id) {
        removed = cloneNode(node);
        continue;
      }

      let updatedChildren = node.children;
      if (!removed && node.children && node.children.length) {
        const childResult = walk(node.children);
        if (childResult.removed) {
          removed = childResult.removed;
          updatedChildren = childResult.list;
        }
      }

      if (removed) {
        next.push({ ...node, children: updatedChildren });
      } else if (updatedChildren !== node.children) {
        next.push({ ...node, children: updatedChildren });
      } else {
        next.push(node);
      }
    }

    return { list: removed ? normalizeOrder(next) : next, removed };
  };

  return walk(nodes);
};

const insertAsChild = (nodes, parentId, node) => {
  const walk = (list) => {
    let inserted = false;
    const next = list.map((curr) => {
      if (curr.id === parentId) {
        inserted = true;
        const children = normalizeOrder([...(curr.children || []), cloneNode(node)]);
        return { ...curr, children };
      }
      if (!inserted && curr.children && curr.children.length) {
        const childResult = walk(curr.children);
        if (childResult.inserted) {
          inserted = true;
          return { ...curr, children: childResult.list };
        }
      }
      return curr;
    });
    return { list: inserted ? normalizeOrder(next) : next, inserted };
  };

  const result = walk(nodes);
  return { list: result.inserted ? result.list : nodes, inserted: result.inserted };
};

const insertAsSibling = (nodes, targetId, node, position = 'after') => {
  const walk = (list) => {
    const idx = list.findIndex((n) => n.id === targetId);
    if (idx !== -1) {
      const next = [...list];
      const insertIndex = position === 'before' ? idx : idx + 1;
      next.splice(insertIndex, 0, cloneNode(node));
      return { list: normalizeOrder(next), inserted: true };
    }

    for (let i = 0; i < list.length; i += 1) {
      const curr = list[i];
      if (!curr.children || !curr.children.length) continue;
      const childResult = walk(curr.children);
      if (childResult.inserted) {
        const next = [...list];
        next[i] = { ...curr, children: childResult.list };
        return { list: normalizeOrder(next), inserted: true };
      }
    }

    return { list, inserted: false };
  };

  return walk(nodes);
};

export const menuThemeSlice = createSlice({
  name: 'menuTheme',
  initialState: {
    tree: [],
    initialTree: [],
    loading: false,
    error: null,
    lastSaved: null,
    skipNextReload: false,
    skipNextMenusRefresh: false,
    dirty: false
  },
  reducers: {
    setMenuTree: (state, action) => {
      state.tree = action.payload || [];
      state.initialTree = state.tree.map((n) => n);
      state.dirty = false;
    },
    setSkipNextReload: (state, action) => {
      state.skipNextReload = !!action.payload;
    },
    setSkipNextMenusRefresh: (state, action) => {
      state.skipNextMenusRefresh = !!action.payload;
    },
    updateNodeLabel: (state, action) => {
      const { id, label, keyOverride, finalizeDraft } = action.payload;
      state.tree = renameNode(state.tree, { id, newLabel: label, keyOverride, finalizeDraft });
      state.dirty = true;
    },
    removeNode: (state, action) => {
      state.tree = toggleNodeHidden(state.tree, action.payload, true);
      state.dirty = true;
    },
    setNodeHidden: (state, action) => {
      const { id, hidden } = action.payload;
      state.tree = toggleNodeHidden(state.tree, id, hidden);
      state.dirty = true;
    },
    updateChildrenVisibility: (state, action) => {
      const { parentId, visibility } = action.payload;
      state.tree = toggleChildrenVisibility(state.tree, parentId, visibility);
      state.dirty = true;
    },
    updateNodeChildren: (state, action) => {
      const { id, children } = action.payload;
      const recur = (nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return { ...node, children: normalizeOrder(children) };
          }
          if (node.children && node.children.length) {
            return { ...node, children: recur(node.children) };
          }
          return node;
        });
      state.tree = recur(state.tree);
      state.dirty = true;
    },
    addNode: (state, action) => {
      const { parentId = null, node } = action.payload || {};
      if (!node?.id) return;
      const baseNode = {
        ...node,
        order: node.order ?? 0,
        children: node.children ? normalizeOrder(node.children) : []
      };
      if (parentId == null) {
        state.tree = normalizeOrder([...(state.tree || []), baseNode]);
      } else {
        const { list, inserted } = insertAsChild(state.tree, parentId, baseNode);
        if (!inserted) return;
        state.tree = list;
      }
      state.dirty = true;
    },
    moveNodeToParent: (state, action) => {
      const { id, newParentId } = action.payload;
      if (id === newParentId) return;
      const { list, removed } = detachNode(state.tree, id);
      if (!removed) return;
      if (newParentId == null) {
        state.tree = normalizeOrder([...list, removed]);
        state.dirty = true;
        return;
      }
      if (isDescendantNode(removed, newParentId)) return;
      const parentExists = findNodeById(list, newParentId);
      if (!parentExists) return;
      const { list: updatedTree, inserted } = insertAsChild(list, newParentId, removed);
      if (!inserted) return;
      state.tree = updatedTree;
      state.dirty = true;
    },
    moveNodeRelative: (state, action) => {
      const { sourceId, targetId, intent } = action.payload; // intent: 'before' | 'after' | 'child'
      if (sourceId === targetId) return;
      const targetNode = findNodeById(state.tree, targetId);
      if (!targetNode) return;
      const sourceSnapshot = findNodeById(state.tree, sourceId);
      if (sourceSnapshot && isDescendantNode(sourceSnapshot, targetId)) return;
      const { list, removed } = detachNode(state.tree, sourceId);
      if (!removed) return;
      if (intent === 'child') {
        const { list: updatedTree, inserted } = insertAsChild(list, targetId, removed);
        if (!inserted) return;
        state.tree = updatedTree;
      } else {
        const { list: updatedTree, inserted } = insertAsSibling(list, targetId, removed, intent === 'before' ? 'before' : 'after');
        if (!inserted) return;
        state.tree = updatedTree;
      }
      state.dirty = true;
    },
    revertMenuTree: (state) => {
      state.tree = state.initialTree.map((n) => n);
      state.dirty = false;
    },
    moveNode: (state, action) => {
      const { id, direction } = action.payload;
      state.tree = moveNodeInSiblings(state.tree, id, direction);
      state.dirty = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMenuThemeLatest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMenuThemeLatest.fulfilled, (state, action) => {
        state.loading = false;
        state.tree = action.payload || [];
        state.initialTree = state.tree.map((n) => n);
        state.dirty = false;
      })
      .addCase(loadMenuThemeLatest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Load failed';
      })
      .addCase(persistMenuTheme.pending, (state) => {
        state.error = null;
      })
      .addCase(persistMenuTheme.fulfilled, (state, action) => {
        state.lastSaved = action.payload;
        state.initialTree = state.tree.map((n) => n);
        state.dirty = false;
      })
      .addCase(persistMenuTheme.rejected, (state, action) => {
        state.error = action.payload || 'Save failed';
      });
  }
});

export const {
  setMenuTree,
  updateNodeLabel,
  removeNode,
  updateNodeChildren,
  setSkipNextReload,
  setSkipNextMenusRefresh,
  addNode,
  moveNodeToParent,
  moveNodeRelative,
  revertMenuTree,
  moveNode,
  setNodeHidden,
  updateChildrenVisibility
} = menuThemeSlice.actions;
export default menuThemeSlice.reducer;
